import { BaseButton, BaseDialog, BaseInput, BaseTextarea } from '@/shared/components'
import { validateGUID } from '@/shared/lib/guid'
import { convertFileToBase64, FileData, validateFile } from '@/shared/utils'
import { CirclePlus } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { NewDisputModel } from '../../model'
import TrashIcon from '@/shared/assets/icons/trash.svg?react'

type FormData = {
  paymentId: string
  disputAmountFromMerchant: string
  receiptUrl: string
  description: string
}

type Props = {
  modalVisible: boolean
  setModalVisible: (value: boolean) => void
  onSubmit: (data: NewDisputModel) => void
  isLoading?: boolean
  paymentIdFromProps?: string
}

export const CreateDisputeDialog = ({
  modalVisible,
  setModalVisible,
  onSubmit,
  isLoading,
  paymentIdFromProps,
}: Props) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      paymentId: '',
      disputAmountFromMerchant: '',
      receiptUrl: '',
      description: '',
    },
  })

  useEffect(() => {
    if (modalVisible && paymentIdFromProps) {
      reset((prev) => ({
        ...prev,
        paymentId: paymentIdFromProps,
      }))
    }
  }, [modalVisible, paymentIdFromProps, reset])

  const [uploadedFile, setUploadedFile] = useState<FileData | null>(null)
  const [fileError, setFileError] = useState<string | undefined>(undefined)
  const [isUploading, setIsUploading] = useState(false)

  const handleFileUpload = useCallback(async (file: File) => {
    setFileError(undefined)
    setIsUploading(true)

    try {
      const validationError = validateFile(file)
      if (validationError) {
        setFileError(validationError)
        return
      }

      const fileData = await convertFileToBase64(file)
      setUploadedFile(fileData)
    } catch (error) {
      setFileError('Ошибка при загрузке файла')
    } finally {
      setIsUploading(false)
    }
  }, [])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handleFileUpload(file)
    }
    // Сбрасываем значение input для возможности повторной загрузки того же файла
    event.target.value = ''
  }

  const handleFileRemove = () => {
    setUploadedFile(null)
    setFileError(undefined)
  }

  const handleClose = () => {
    reset()
    setUploadedFile(null)
    setFileError(undefined)
    setModalVisible(false)
  }

  const handleFormSubmit = (data: FormData) => {
    if (!uploadedFile) {
      setFileError('Необходимо загрузить файл чека')
      return
    }

    const requestData: NewDisputModel = {
      paymentId: data.paymentId,
      receiptFile: uploadedFile.base64,
      disputAmountFromMerchant: parseFloat(data.disputAmountFromMerchant) || 0,
      receiptType: uploadedFile.type,
      receiptUrl: data.receiptUrl,
      description: data.description,
    }

    onSubmit(requestData)
  }

  return (
    <BaseDialog
      isVisible={modalVisible}
      onClose={handleClose}
      onAction={handleSubmit(handleFormSubmit)}
      title={'Создание заявки'}
      actionButtonName={'Создать'}
      isLoading={isLoading}
    >
      <div className="max-h-[600px] overflow-y-auto scrollbar-hide">
        <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-y-4 p-0.5">
          <BaseInput
            label={'ID платежа'}
            placeholder="0197a617-361b-7294-8089-78b66473389f"
            {...register('paymentId', {
              required: 'ID платежа обязателен для заполнения',
              validate: validateGUID,
            })}
            variant="default"
            disabled={!!paymentIdFromProps}
            error={errors.paymentId?.message}
          />

          <BaseInput
            label={'Сумма чека'}
            placeholder="Введите сумму"
            {...register('disputAmountFromMerchant', {
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: 'Введите корректную сумму',
              },
              min: {
                value: 0.01,
                message: 'Сумма должна быть больше 0',
              },
            })}
            variant="secondary"
            error={errors.disputAmountFromMerchant?.message}
          />

          {/* Загрузка файла */}
          <div className="flex flex-col gap-2">
            <label className="font-medium text-sm">Прикрепленный чек</label>

            {!uploadedFile ? (
              <div className="relative">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                  disabled={isUploading}
                />
                <BaseButton
                  variant="transparent"
                  padding="xs"
                  className="disabled:opacity-50 text-primary hover:text-primary-hover dark:hover:text-primary-dark-hover dark:text-primary-dark"
                >
                  <CirclePlus width={20} height={20} />
                  {isUploading ? 'Загрузка...' : 'Прикрепить файл'}
                </BaseButton>
              </div>
            ) : (
              <div className="flex justify-between items-center bg-light-purple dark:bg-dark-purple p-3 rounded-lg">
                <div className="flex items-center gap-3">
                  <BaseButton variant="ghost" size="icon" onClick={handleFileRemove}>
                    <div className="flex justify-center items-center bg-[#e5e5ff] rounded-full min-w-11 h-11">
                      <TrashIcon className="text-error" />
                    </div>
                  </BaseButton>

                  <div>
                    <p className="font-medium text-sm">{uploadedFile.name}</p>
                    <p className="text-xs">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
              </div>
            )}

            {fileError && <p className="text-error text-sm">{fileError}</p>}
          </div>

          <BaseInput
            label={'URL чека'}
            placeholder="https://example.com/receipt.pdf"
            {...register('receiptUrl')}
            variant="secondary"
            error={errors.receiptUrl?.message}
          />

          <div className="flex flex-col gap-2">
            <label className="font-medium text-sm">Укажите комментарий</label>
            <BaseTextarea {...register('description')} placeholder="Ввести" size="sm" />
          </div>
        </form>
      </div>
    </BaseDialog>
  )
}
